import React, { useEffect, useState } from "react";
import { Column, Row, Text, Button, Card, Modal, Select, SelectOption } from "@amzn/meridian";
import { useAppSelector, useAppDispatch } from "src/store/store";
import SimpleCamera from "./SimpleCamera";
import { resetStreamingState } from "src/store/inspectionCaptureSlice";
import { setShownHumanText, setShowWorkflowHeader } from "src/store/workflowSlice";
import InspectionService from "src/services/inspectionService";

interface QuestionStepProps {
  questionId: string;
  onComplete: () => void;
}

const QuestionStep: React.FC<QuestionStepProps> = ({
  questionId,
  onComplete,
}) => {
  const dispatch = useAppDispatch();
  const { questions, inspectionId } = useAppSelector((state) => state.workflow);
  const { screenWidth, screenHeight, scannedBarcode} = useAppSelector((state) => state.inspectionCapture);
  const [showCamera, setShowCamera] = useState<boolean>(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string>("");
  const [capturedImage, setCapturedImage] = useState<File | null>(null);

  const question = questions.find((q) => q.id === questionId);

  useEffect(() => {
    if (!question) return;

    // Set the human text for display in the header
    dispatch(setShownHumanText(question.text));
    dispatch(setShowWorkflowHeader(false));

    // Cleanup: reset state when component unmounts
    return () => {
      dispatch(resetStreamingState());
      if (question.mode === 'manual') {
        dispatch(setShowWorkflowHeader(true));
      }
    };
  }, [dispatch, question]);

  if (!question) {
    return (
      <Column alignmentHorizontal="center" spacingInset="500">
        <Card spacingInset="500" width="800px">
          <Column spacing="400">
            <Text type="h300">Question Not Found</Text>
            <Text type="b200">Question ID: {questionId}</Text>
            <Row alignmentHorizontal="end">
              <Button type="primary" onClick={onComplete}>
                Continue
              </Button>
            </Row>
          </Column>
        </Card>
      </Column>
    );
  }

  const handleSubmit = async () => {
    if (!selectedAnswer) return;
    const analyzePayload = new FormData();
    analyzePayload.append("inspection_id", inspectionId);
    analyzePayload.append("question_id", questionId);
    analyzePayload.append("confirmed_result", selectedAnswer);
    analyzePayload.append("is_compliant", selectedAnswer);
    if(scannedBarcode !== null) {
      analyzePayload.append("object_id", scannedBarcode);
    }
    if (capturedImage) {
      analyzePayload.append("file", capturedImage);
    }
    await InspectionService.recordManualResult(analyzePayload);
    // Restore workflow header before completing
    dispatch(setShowWorkflowHeader(true));
    onComplete();
  };

  const openCamera = () => {
    setShowCamera(true);
  };

  const handleCameraCapture = (file: File) => {
    setCapturedImage(file);
    // First hide the camera to trigger cleanup
    setShowCamera(false);
    
    // Wait a brief moment for cleanup to complete before advancing workflow
    setTimeout(() => {
      // Automatically submit after capture
      handleSubmit();
    }, 100); // Small delay to ensure camera cleanup completes
  };

  const handleCameraCancel = () => {
    setShowCamera(false);
  };

  return (
    <>
      <Modal open={!showCamera} title="Manual/Image Question">
        <Column spacing="400">
          <Text>{question.text}</Text>

          <Select
            value={selectedAnswer}
            onChange={(value) => setSelectedAnswer(value)}
            placeholder="Select an answer"
          >
            <SelectOption value="Yes" label="Yes"/>
            <SelectOption value="No" label="No"/>
          </Select>

          <Row alignmentHorizontal="end">
            {question.mode === 'manual' && (
              <Button
                type="primary"
                onClick={handleSubmit}
                disabled={!selectedAnswer}
              >
                Continue 
              </Button>
            )}
            {question.mode !== 'manual' && (
              <Button
                type="primary"
                onClick={openCamera}
                disabled={!selectedAnswer}
              >
                Take Picture and Continue
              </Button>
            )}
          </Row>
        </Column>
      </Modal>
      
      {showCamera && (
        <SimpleCamera
          onCapture={handleCameraCapture}
          onCancel={handleCameraCancel}
          screenWidth={screenWidth}
          screenHeight={screenHeight}
        />
      )}
    </>
  );
};

export default QuestionStep;
